import type { User } from '../types/user';

/**
 * API User type as returned from OAuth callback endpoints
 */
export interface ApiUser {
  id: string;
  email: string;
  username?: string;
  name?: string;
  avatar_url?: string;
  google_id?: string;
  facebook_id?: string;
  spotify_id?: string;
  tidal_id?: string;
  tier: string;
  tier_expires_at?: string | null;
  subscription_status: string;
  ai_planner_used: number;
  ai_planner_limit: number;
  hidden_pearl_used: number;
  hidden_pearl_limit: number;
  reset_usage_at: string;
  is_active: boolean;
  is_admin: boolean;
  created_at: string;
  updated_at: string;
  last_login_at?: string;
  last_active_at?: string;
  email_verified_at?: string;
  preferences?: Record<string, unknown>;
  metadata: Record<string, unknown>;
  password_hash?: string;
  tierConfig?: {
    name?: string;
    displayName?: string;
    mainPOIsPerDay?: number;
    tokenBudgetPerTrip?: number;
    maxDaysPerPlan?: number;
    maxPlansPerMonth?: number;
    hiddenGemsLimit?: number;
    replanning?: number;
  };
}

/**
 * API response wrapper type for user endpoints
 */
export interface ApiUserResponse {
  user?: ApiUser;
  message?: string;
}

/**
 * Type guard to check if input is ApiUserResponse
 */
function isApiUserResponse(
  input: ApiUser | ApiUserResponse,
): input is ApiUserResponse {
  return 'user' in input || 'message' in input;
}

/**
 * Maps API user data to internal User type
 * Handles date string conversions and type assertions
 */
export function mapApiUserToUser(apiUser: ApiUser): User;
export function mapApiUserToUser(apiResponse: ApiUserResponse): User;
export function mapApiUserToUser(input: ApiUser | ApiUserResponse): User {
  let apiUser: ApiUser;

  if (isApiUserResponse(input)) {
    // This is an ApiUserResponse
    if (!input.user) {
      throw new Error('No user data found in response');
    }
    apiUser = input.user;
  } else {
    // This is a direct ApiUser
    apiUser = input;
  }

  return {
    id: apiUser.id,
    email: apiUser.email,
    name: apiUser.name,
    avatar_url: apiUser.avatar_url,
    tier: apiUser.tier as any, // Type assertion for tier - API returns string but we expect UserTierType
    tier_expires_at: apiUser.tier_expires_at
      ? new Date(apiUser.tier_expires_at)
      : undefined,
    google_id: apiUser.google_id,
    facebook_id: apiUser.facebook_id,
    spotify_id: apiUser.spotify_id,
    tidal_id: apiUser.tidal_id,
    ai_planner_used: apiUser.ai_planner_used,
    ai_planner_limit: apiUser.ai_planner_limit,
    hidden_pearl_used: apiUser.hidden_pearl_used,
    hidden_pearl_limit: apiUser.hidden_pearl_limit,
    reset_usage_at: new Date(apiUser.reset_usage_at),
    created_at: new Date(apiUser.created_at),
    updated_at: new Date(apiUser.updated_at),
    last_login_at: apiUser.last_login_at
      ? new Date(apiUser.last_login_at)
      : undefined,
    last_active_at: apiUser.last_active_at
      ? new Date(apiUser.last_active_at)
      : undefined,
    is_active: apiUser.is_active,
    is_admin: apiUser.is_admin,
    metadata: apiUser.metadata,
    preferences: apiUser.preferences,
    subscription_status: apiUser.subscription_status,
    password_hash: apiUser.password_hash,
    username: apiUser.username,
    email_verified_at: apiUser.email_verified_at
      ? new Date(apiUser.email_verified_at)
      : undefined,
    tierConfig: apiUser.tierConfig,
  };
}
